import * as React from 'react';
import PropTypes from 'prop-types';
import {getTeaching} from './updatables';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { visuallyHidden } from '@mui/utils';
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useMediaQuery } from 'react-responsive';

function descendingComparator(a, b, orderBy) {
  if (a[orderBy] === "-" && b[orderBy] !== "-") {return -1;}
  if (b[orderBy] === "-" && a[orderBy] !== "-") {return 1;}
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
  {
    id: 'course',
    numeric: false,
    disablePadding: true,
    label: (<div style={{fontFamily: "mainFont", marginLeft: "8px"}}>Course</div>),
    width: "25%"
  },
  {
    id: 'role',
    numeric: true,
    disablePadding: false,
    label: 'Role',
    width: "20%"
  },
  {
    id: 'semesters',
    numeric: true,
    disablePadding: false,
    label: 'Semesters',
    width: "20%"
  },
  {
    id: 'institution',
    numeric: true,
    disablePadding: false,
    label: 'Institution',
    width: "20%"
  },
  {
    id: 'links',
    numeric: true,
    disablePadding: false,
    label: 'Links',
    width: "15%"
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            sx={{ width: headCell.width }}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
};

function Row({ row }) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <TableRow
        sx={{
          transition: "background-color .3s ease",
          "&:hover": {
            backgroundColor: "#F0B6D520",
          },
        }}
      >
        <TableCell scope="row" sx={{ width: "35%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: "mainFontItalic" }}>
            {row.course}
          </Box>
        </TableCell>

        <TableCell align="right" sx={{ width: "25%" }}>
          {row.role}
        </TableCell>

        <TableCell align="right" sx={{ width: "10%" }}>
          {row.semesters}
        </TableCell>

        <TableCell align="right" sx={{ width: "10%" }}>
          {row.institution}
        </TableCell>

        <TableCell align="right" sx={{ width: "20%" }}>
          {row.links?.map((item, index) => (
            <a
              key={index}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "block",
                color: "#d866a3",
                textDecoration: "underline",
                fontFamily: "mainFontPar",
                cursor: "pointer",
                margin: 0,
                padding: 0,
              }}
            >
              {item.text}
            </a>
          ))}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
            colSpan={5}
            sx={{
                p: 0,
                borderBottom: open ? "1px solid rgba(224,224,224,1)" : 0,
            }}
        >
        </TableCell>
      </TableRow>
    </>
  );
}

Row.propTypes = {
  row: PropTypes.object.isRequired,
};

export default function EnhancedTable() {
  const [order, setOrder] = React.useState('id');
  const [orderBy, setOrderBy] = React.useState('year');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const isSmallScreen = useMediaQuery({ maxWidth: 670 });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClick = (event, id) => {
    
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 25));
    setPage(0);
  };

  const [teaching, setTeaching] = React.useState([]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - teaching.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...teaching]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [teaching, order, orderBy, page, rowsPerPage],
  );

  React.useEffect(() => {
      async function loadTeaching() {
        const data = await getTeaching();
        setTeaching(data);
      }
  
      loadTeaching();
    }, []);

  return (
    <Box sx={{width: "90%"}}>
        <TableContainer style={{marginLeft: isSmallScreen ? "0%" : "32px"}}>
          <Table
            sx={{ width: isSmallScreen ? "200%" : "100%", tableLayout: "fixed",
                "& .MuiTableCell-head": {
                    fontFamily: "mainFont",
                    fontSize: isSmallScreen ? "8px" : "14px"
                    },
                    "& .MuiTableCell-body": {
                        fontFamily: "mainFontPar",
                        color: "#7c7788",
                        fontSize: isSmallScreen ? "8px" : "14px"
                    },
                    "& th.MuiTableCell-body": {
                        fontFamily: "mainFontItalic",
                        color: "#7c7788",
                        fontSize: isSmallScreen ? "8px" : "14px"
                    },
                    "& .MuiTableSortLabel-root": {
                        fontFamily: "mainFont",
                        fontWeight: 800,
                        color: "#7c7788",
                        fontSize: isSmallScreen ? "8px" : "14px"
                    },
                    "& .MuiTableSortLabel-root.Mui-active": {
                        fontFamily: "mainFont",
                        fontWeight: 800,
                        color: "#7c7788",
                        fontSize: isSmallScreen ? "8px" : "14px"
                    },
                }}
            aria-labelledby="tableTitle"
            size={'small'}
          >
            <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
            />
            <TableBody>
                {visibleRows.map((row) => (
                    <Row
                        key={row.title}
                        row={row}
                    />
                ))}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (33) * emptyRows,
                  }}
                >
                  <TableCell colSpan={4} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={teaching.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
        />
    </Box>
  );
}
